"use client";

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { motion, useReducedMotion } from "@/lib/motion";
import { ChevronDown } from "@/components/icons";
import { intersectionObserverScrollReveal } from "@/lib/motion-viewport";
import { cn, scrollToId } from "@/lib/utils";
import {
  AnnouncementChip,
  buttonVariants,
  HeroGlow,
  StatCard,
  ReviewBadges,
  TrustBenefitList,
} from "@/components/ui";
import { ROUTES } from "@/lib/constants";
import type { ProductHeroBaseProps } from "./types";

export default function ProductHeroBase({
  headingId,
  eyebrow: _eyebrow,
  headingLine1,
  headingLine2,
  description,
  trustBadges,
  stats,
  statsAriaLabel = "Key statistics",
  ctas,
  scrollIndicator,
  showMetricEyebrow,
  proxyTester,
  socialProofVariant = "compact",
  textClassName,
  announcement,
}: ProductHeroBaseProps) {
  const prefersReducedMotion = useReducedMotion();
  const [statsVisible, setStatsVisible] = useState(false);
  const statsRef = useRef<HTMLDivElement>(null);

  const showStats = !proxyTester;

  const smoothAnchorClick = (href: string) => (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    scrollToId(href.slice(1));
  };

  const ctasBlock =
    ctas && ctas.length > 0 ? (
      <div className="flex flex-wrap items-center justify-center gap-3">
        {ctas.map((cta, i) => {
          const isPrimary = i === 0;
          const isAnchor = cta.href.startsWith("#");
          const useGlow = isPrimary && !!proxyTester;
          const content = (
            <>
              {cta.leadingIcon}
              {cta.label}
              {cta.trailingIcon}
            </>
          );

          if (useGlow) {
            const Inner = isAnchor ? "a" : Link;
            const innerProps = isAnchor
              ? { href: cta.href, onClick: smoothAnchorClick(cta.href) }
              : { href: cta.href };
            return (
              <span key={cta.href} className="cta-glow-btn-wrap">
                <Inner {...innerProps} className="cta-glow-btn-inner">
                  {content}
                </Inner>
              </span>
            );
          }

          const className = cn(
            buttonVariants({ variant: isPrimary ? "default" : "outline", size: "lg" }),
            "font-semibold px-6",
            isPrimary && "bg-primary text-primary-foreground border-transparent"
          );
          return isAnchor ? (
            <a
              key={cta.href}
              href={cta.href}
              onClick={smoothAnchorClick(cta.href)}
              className={className}
            >
              {content}
            </a>
          ) : (
            <Link key={cta.href} href={cta.href} className={className}>
              {content}
            </Link>
          );
        })}
      </div>
    ) : null;

  useEffect(() => {
    const el = statsRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) setStatsVisible(true);
    }, intersectionObserverScrollReveal);
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const heroLayout = proxyTester
    ? "pt-24 sm:pt-24 pb-10 sm:pb-14 flex flex-col sm:min-h-[100svh] 3xl:min-h-[calc(100svh-18rem)]"
    : "pt-24 sm:pt-32 pb-14 sm:pb-20";

  return (
    <section
      className={cn("border-border relative overflow-y-clip border-b", heroLayout)}
      aria-labelledby={headingId}
    >
      <HeroGlow align="center" />

      <div
        className={cn(
          "mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8",
          proxyTester && "flex flex-1 flex-col justify-center"
        )}
      >
        <div className={cn("mx-auto max-w-3xl text-center", textClassName)}>
          {socialProofVariant === "compact" && (
            <div
              className="mb-6 flex flex-wrap items-center justify-center gap-3"
              aria-label="Review platforms"
            >
              <ReviewBadges />
            </div>
          )}

          {announcement && (
            <div className="mb-5 flex justify-center">
              <AnnouncementChip href={announcement.href} label={announcement.label} />
            </div>
          )}

          <h1
            id={headingId}
            className="text-foreground mb-6 text-4xl leading-[1.1] font-bold tracking-tight sm:text-5xl lg:text-6xl"
          >
            {headingLine1}
            <br />
            {headingLine2}
          </h1>

          <p className="text-muted-foreground light:text-foreground/80 mx-auto mb-4 max-w-4xl text-lg leading-relaxed sm:mb-4">
            {description}
          </p>

          {proxyTester && (
            <div className="mb-8 flex w-full items-center justify-center sm:mb-6 sm:min-h-[56px]">
              {ctasBlock && (
                <motion.div
                  initial={prefersReducedMotion ? false : { opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
                  className="relative flex flex-col items-center"
                >
                  <div className="relative">{ctasBlock}</div>
                  <TrustBenefitList className="mt-4 flex flex-wrap items-center justify-center gap-x-6 gap-y-2" />
                </motion.div>
              )}
            </div>
          )}

          {!proxyTester && (
            <div
              className={cn(
                "flex flex-col-reverse items-center gap-y-6 md:flex-col md:gap-y-10",
                ctas && ctas.length > 0 ? "mb-14" : "mb-12"
              )}
            >
              <motion.div
                initial={prefersReducedMotion ? false : { opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.45, delay: 0.22 }}
                className="flex flex-wrap items-center justify-center gap-3"
                aria-label="Trust signals"
              >
                {trustBadges.flatMap((badge) => {
                  const isMoneyBack = badge.toLowerCase().includes("money-back");
                  if (isMoneyBack) {
                    return [
                      <span
                        key={badge}
                        className="border-border bg-muted/40 text-foreground flex items-center rounded-full border px-3 py-1.5 text-xs font-semibold"
                      >
                        30-day money-back guarantee
                        <span className="group/mbtooltip relative ml-0.5 cursor-help">
                          <span
                            className="text-base leading-none"
                            style={{ color: "var(--brand-green)" }}
                          >
                            *
                          </span>
                          <span className="text-foreground bg-popover border-border pointer-events-none absolute bottom-full left-1/2 z-50 mb-2 w-72 -translate-x-1/2 rounded-lg border px-3 py-2 text-center text-xs leading-relaxed font-normal whitespace-normal normal-case opacity-0 shadow-lg transition-opacity duration-200 group-hover/mbtooltip:opacity-100">
                            All proxies come with a 30-day money-back guarantee conditional upon
                            using less than 10 GB of data.
                          </span>
                        </span>
                      </span>,
                      <span
                        key="24-7-support"
                        className="border-border bg-muted/40 text-foreground hidden items-center rounded-full border px-3 py-1.5 text-xs font-semibold md:flex"
                      >
                        Premium support
                      </span>,
                    ];
                  }
                  const isKyc = badge.toLowerCase().includes("kyc");
                  if (isKyc) {
                    return [
                      <Link
                        key={badge}
                        href={ROUTES.kyc}
                        className="border-border bg-muted/40 text-foreground hover:border-brand-green hidden items-center rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors md:flex"
                      >
                        {badge}
                      </Link>,
                    ];
                  }
                  return [
                    <span
                      key={badge}
                      className="border-border bg-muted/40 text-foreground hidden items-center rounded-full border px-3 py-1.5 text-xs font-semibold md:flex"
                    >
                      {badge}
                    </span>,
                  ];
                })}
              </motion.div>

              {!proxyTester && ctasBlock && (
                <motion.div
                  initial={prefersReducedMotion ? false : { opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.45, delay: 0.28 }}
                >
                  {ctasBlock}
                </motion.div>
              )}
            </div>
          )}

          {showStats && (
            <motion.div
              ref={statsRef}
              initial={prefersReducedMotion ? false : { opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.35 }}
              className="mx-auto grid max-w-lg grid-cols-3 gap-3"
              aria-label={statsAriaLabel}
            >
              {stats.map((stat) => (
                <StatCard key={stat.label} stat={stat} animate={statsVisible} />
              ))}
            </motion.div>
          )}

          {socialProofVariant === "homepage" && (
            <motion.div
              initial={prefersReducedMotion ? false : { opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.45 }}
              className="border-border/50 mx-auto mt-6 flex w-full max-w-xl flex-wrap items-center justify-center gap-3 pl-5"
              aria-label="Review platforms"
            >
              <ReviewBadges />
            </motion.div>
          )}
        </div>

        {scrollIndicator && (
          <motion.div
            initial={prefersReducedMotion ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8, duration: 0.5 }}
            className="mt-14 flex justify-center"
          >
            <a
              href={scrollIndicator.href}
              onClick={
                scrollIndicator.href.startsWith("#")
                  ? smoothAnchorClick(scrollIndicator.href)
                  : undefined
              }
              aria-label={`Scroll to ${scrollIndicator.label}`}
              className="text-muted-foreground hover:text-foreground group flex flex-col items-center gap-1 transition-colors"
            >
              <span className="text-xs font-medium">{scrollIndicator.label}</span>
              <ChevronDown
                size={18}
                className="group-hover:text-brand animate-bounce transition-colors"
                aria-hidden="true"
              />
            </a>
          </motion.div>
        )}
      </div>
    </section>
  );
}
